<template>
  <div>
    <v-checkbox
      v-model="enabled"
      label="Enable paywall"
      color="primary"
      hideDetails
      density="compact"
      @update:modelValue="value => !value && (props.actions.rf_retention_paywall_tracker_id = null)"
    />
    <RfAlert
      v-if="enabled && model.experiment && !model.experiment.is_completed"
      type="warning"
      class="mt-3"
    >
      Experiments have no effect on paywall prompts — every user will receive the original variant.
    </RfAlert>
    <template v-if="enabled">
      <div class="mt-4 flex flex-row gap-4">
        <v-select
          v-model="actions.rf_retention_paywall_tracker_id"
          :items="allPaywallTrackers"
          itemTitle="label"
          itemValue="id"
          label="Paywall tracker"
          variant="outlined"
          density="compact"
          persistentPlaceholder
          class="!basis-full"
        />
        <v-text-field
          v-model.number="actions.rf_retention_paywall_limit"
          label="Free visits"
          type="number"
          variant="outlined"
          density="compact"
          persistentPlaceholder
          class="min-w-28 !basis-0"
        />
      </div>
      <v-select
        v-model="actions.rf_retention_paywall_zone"
        :items="allWebZones"
        itemTitle="name"
        itemValue="zone"
        label="Zone to block"
        variant="outlined"
        density="compact"
        persistentPlaceholder
      />
      <v-select
        v-model="method"
        :items="methodOptions"
        label="Block method"
        variant="outlined"
        density="compact"
        persistentPlaceholder
        class="min-w-40 !basis-0"
      />
      <template v-if="method !== 'custom'">
        <div class="flex flex-row gap-4">
          <v-select
            v-model="target"
            :items="targetOptions"
            label="Apply to elements"
            variant="outlined"
            density="compact"
            persistentPlaceholder
          />
          <v-text-field
            v-if="target !== 'custom'"
            v-model.number="skipEls"
            label="Elements to allow"
            type="number"
            variant="outlined"
            density="compact"
            persistentPlaceholder
            class="min-w-36 !basis-0"
          />
        </div>
        <v-text-field
          v-if="target === 'custom'"
          v-model="customSelector"
          label="CSS selector to target"
          variant="outlined"
          density="compact"
          persistentPlaceholder
          placeholder="div:nth-of-type(n+3)"
        />
      </template>
      <v-textarea
        v-else
        v-model="actions.rf_retention_paywall_css"
        label="Custom CSS to apply"
        variant="outlined"
        density="compact"
        persistentPlaceholder
        rows="4"
      />
      <p class="text-grey-2 -mt-2 px-2">{{ previewDescription }}</p>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCustomFieldsStore } from "@/pinia/customFieldsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import RfAlert from "@/components/alerts/RfAlert.vue";
import { isPaywall } from "@/utils/prompts/promptHelpers";

const props = defineProps({
  actions: { type: Object, required: true },
  model: { type: Object, required: true },
});

const customFieldsStore = useCustomFieldsStore();
const appsStore = useAppsStore();
const allPromptsStore = useAllPromptsStore();

// Tracker & Zone

const enabled = ref(false);
const allPaywallTrackers = computed(
  () =>
    customFieldsStore.segmentCustomFields?.filter(
      field => field.is_usage && field.options?.paywall,
    ) || [],
);
const allWebZones = computed(
  () => allPromptsStore.promptGroups?.filter(zone => zone.device_type === "web") || [],
);
const fetchPaywallTrackers = () => {
  if (customFieldsStore.segmentCustomFields?.length) return;
  customFieldsStore.getAppCustomFields({
    appId: appsStore.app.id,
    fieldType: "custom",
  });
};
const fetchZones = () => {
  if (allPromptsStore.promptGroups?.length) return;
  allPromptsStore.getPromptGroups(appsStore.app.id);
};

// Method & Target

const method = ref("");
const target = ref("");
const skipEls = ref(0);
const customSelector = ref("");

const methodOptions = [
  { title: "Blur", value: "blur" },
  { title: "Hide", value: "hide" },
  { title: "Strip text", value: "strip" },
  { title: "Custom CSS", value: "custom" },
];
const targetOptions = [
  { title: "All child elements (*)", value: "*" },
  { title: "Paragraphs <p>", value: "p" },
  { title: "Custom CSS selector", value: "custom" },
];

const BLUR_CSS = "filter:blur(5px);user-select:none;pointer-events:none;";
const HIDE_CSS = "display:none;";

// Set config from actions
const setInitialValues = () => {
  enabled.value = isPaywall({ actions: props.actions });

  const css = props.actions.rf_retention_paywall_css;
  const stripSelector = props.actions.rf_retention_paywall_strip_text_selector;

  if (stripSelector) {
    method.value = "strip";
    const match = stripSelector.match(/^([*p]):nth-of-type\(n\+(\d+)\)$/);
    if (match) {
      // e.g. "p:nth-of-type(n+1)" -> p, 1
      target.value = match[1];
      skipEls.value = parseInt(match[2]) - 1;
    } else {
      // custom strip selector
      target.value = "custom";
      customSelector.value = stripSelector;
    }
  } else if (css) {
    // e.g. "*:nth-of-type(n+2){filter:blur(5px);user-select:none;}" -> *, 2
    const match = css.match(/^\/\*GENERATED\*\/\n([\s\S]*)\{(.*)\}$/);
    if (match) {
      // Predefined method, e.g. blur or hide
      if (match[2] === BLUR_CSS) method.value = "blur";
      else if (match[2] === HIDE_CSS) method.value = "hide";

      const selectorMatch = match[1].match(/([*p]):nth-of-type\(n\+(\d+)\)/);
      if (selectorMatch) {
        // Predefined selector, e.g. * or p
        target.value = selectorMatch[1];
        skipEls.value = parseInt(selectorMatch[2]) - 1;
      } else {
        // Custom selector
        target.value = "custom";
        customSelector.value = match[1];
      }
    } else {
      // Custom CSS
      method.value = "custom";
    }
  } else {
    // Default to blur
    method.value = "blur";
    target.value = "*";
    skipEls.value = 0;
  }
};

// Set actions when config changed
watch([method, target, skipEls, customSelector], () => {
  if (!enabled.value || method.value === "custom") return;
  const selector =
    target.value === "custom"
      ? customSelector.value
      : `${target.value}:nth-of-type(n+${skipEls.value + 1})`;
  if (method.value === "strip") {
    props.actions.rf_retention_paywall_strip_text_selector = selector;
    props.actions.rf_retention_paywall_css = null;
  } else {
    const css = method.value === "blur" ? BLUR_CSS : HIDE_CSS;
    props.actions.rf_retention_paywall_css = `/*GENERATED*/\n${selector}{${css}}`;
    props.actions.rf_retention_paywall_strip_text_selector = null;
  }
});

const previewDescription = computed(() => {
  if (!enabled.value) return "";

  let text = "";
  if (method.value === "custom") {
    text = "Custom CSS will be applied to the zone";
  } else {
    switch (target.value) {
      case "*":
        text += "Child elements";
        break;
      case "p":
        text += "Paragraphs";
        break;
      case "custom":
        text += "Elements matching the custom selector";
        break;
    }
    text += " in the zone ";
    if (target.value !== "custom" && skipEls.value > 0) {
      text += `(except the first${skipEls.value > 1 ? " " + skipEls.value : ""})`;
    }
    text += " will ";
    switch (method.value) {
      case "blur":
        text += "be blurred";
        break;
      case "hide":
        text += "be hidden";
        break;
      case "strip":
        text += "have text stripped";
        break;
    }
  }
  if (props.actions.rf_retention_paywall_limit > 0) {
    text += ` after the user has visited ${props.actions.rf_retention_paywall_limit} times`;
  }
  return text;
});

onMounted(() => {
  fetchPaywallTrackers();
  fetchZones();
  setInitialValues();
});
</script>
